import {
  Button,
  ButtonGroup,
  Icon,
  Menu,
  MenuItem,
  MenuPopup,
  MenuTrigger,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  useIsMobile,
  type Omittable,
} from "@salestrak/ui";

import { HotkeyTooltipLabel } from "#app/components/keyboard-shortcuts/hotkey-tooltip-label";
import {
  SAVE_VIEW_SHORTCUT,
  useSaveViewHotkey,
} from "#app/lib/hooks/use-save-view-hotkey";

/**
 * THE unsaved-changes control every view kind wears: a warning split button
 * whose presence is the unsaved-state indicator. Three outcomes, one control:
 * keep the change on this view (the button, or ⌘/Ctrl+Enter), keep it as a
 * view of its own (the menu), or put it back (the menu).
 *
 * The label carries no edit count — "there are changes" is the whole message.
 * On the compact shell tier the face is the save glyph alone beside the
 * chevron; the tooltip names the action and its chord on every tier.
 *
 * Presentational: the caller decides WHEN it mounts — only while dirty, and
 * only for a viewer who may save at all. A viewer without the right to save
 * gets no control: their edits are previews (the Filters page and the
 * toolbar toggles are open to everyone), reset by their own controls or by
 * leaving the view, and a Save they cannot press is an action they cannot
 * take. The saved-view mount is `ViewSaveButton`; a dashboard mounts it
 * directly with its staged board + display options behind `onSave`.
 */
export function SaveViewControl({
  canSave,
  saving = false,
  tooltip = "Save this view",
  onSave,
  onSaveAsNewTab,
  onDiscard,
}: Omittable<{
  /** False disables Save (and unbinds the chord) for the moment — a fork in
   *  flight, or a dashboard whose staged board is savable while its display
   *  edits are not — leaving the menu's Discard reachable. Not the
   *  permission gate: a viewer who may never save gets no control. */
  canSave: boolean;
  /** A save in flight — the button spins and the chord is inert. */
  saving?: boolean;
  /** What Save does, as the tooltip sentence ("Save this view", "Save for
   *  everyone on Roofing"). The chord keycaps follow it. */
  tooltip?: string;
  /** `via` is "button" or "hotkey", for `view.save`. */
  onSave: (via: "button" | "hotkey") => void;
  /** Route the draft into a brand-new saved tab. Omitted, the menu has no
   *  such row (no views scope, or nothing a new tab could carry). */
  onSaveAsNewTab?: () => void;
  /** Put the view back the way it was saved. */
  onDiscard: () => void;
}>) {
  const isMobile = useIsMobile();
  const pressable = canSave && !saving;

  // The chord lives and dies with the control: bound only while Save itself
  // is pressable, so it can never commit what the button could not.
  useSaveViewHotkey(pressable ? () => onSave("hotkey") : null);

  return (
    <Menu>
      <ButtonGroup>
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                color="warning"
                variant="outline"
                size={isMobile ? "icon-xs" : "xs"}
                shape="square"
                disabled={!pressable}
                loading={saving}
                aria-label={isMobile ? tooltip : undefined}
                onClick={() => onSave("button")}
              >
                {isMobile ? <Icon name="save" /> : "Save view"}
              </Button>
            }
          />
          <TooltipPopup side="top">
            <HotkeyTooltipLabel label={tooltip} keys={SAVE_VIEW_SHORTCUT} />
          </TooltipPopup>
        </Tooltip>
        <MenuTrigger
          disabled={saving}
          render={
            <Button
              aria-label="More save options"
              size="icon-xs"
              shape="square"
              color="warning"
              variant="outline"
            />
          }
        >
          <Icon name="chevron-down" />
        </MenuTrigger>
      </ButtonGroup>
      <MenuPopup align="end">
        {onSaveAsNewTab ? (
          <MenuItem onClick={onSaveAsNewTab}>
            <Icon name="plus" size="md" color="muted" />
            Save as new tab
          </MenuItem>
        ) : null}
        <MenuItem onClick={onDiscard}>
          <Icon name="reload" size="md" color="muted" />
          Discard changes
        </MenuItem>
      </MenuPopup>
    </Menu>
  );
}
